import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { DownloadSimple, ShieldCheck, FileText, Gavel, Truck, ArrowRight } from "@phosphor-icons/react";
import { PageHero } from "../components/PageHero";
import { useSettings } from "../lib/api";

const NAICS_LABELS: Record<string, string> = {
  "236110": "Residential Building Construction",
  "236220": "Commercial & Institutional Building Construction",
  "236118": "Residential Remodelers",
  "238350": "Finish Carpentry Contractors",
  "238330": "Flooring Contractors",
  "238320": "Painting & Wall Covering Contractors",
  "238340": "Tile & Terrazzo Contractors",
  "238390": "Other Building Finishing Contractors",
};

const differentiators = [
  { icon: ShieldCheck, title: "SDVOSB Set-Aside Eligible", text: "Verified service-disabled veteran-owned status for sole-source and set-aside opportunities." },
  { icon: FileText, title: "Compliance Rigor", text: "Davis-Bacon, buy-American, and federal reporting requirements handled as standard procedure." },
  { icon: Gavel, title: "Contract Vehicle Ready", text: "Structured for IDIQ, task orders, BPAs, and teaming arrangements with primes." },
  { icon: Truck, title: "Rapid Mobilization", text: "Local crews, vetted subcontractor network, and emergency-response readiness across Southeast Michigan." },
];

export default function Government() {
  const { data: s } = useSettings();

  return (
    <div data-testid="government-page">
      <PageHero
        overline="Government Contracting"
        title="A partner agencies can put on contract with confidence."
        subtitle="FOSCHE delivers construction, maintenance, and facility support to federal, state, and municipal customers as a certified SDVOSB."
      />

      <section className="bg-canvas py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-5 gap-14">
          <div className="lg:col-span-3">
            <p className="overline-label mb-4">Capability Statement</p>
            <h2 className="font-heading font-medium text-3xl md:text-4xl tracking-tight text-ink">
              Core competencies on one page.
            </h2>
            <p className="mt-5 text-sub leading-relaxed max-w-xl">
              Download our capability statement for a procurement-ready summary of core competencies, differentiators,
              codes, and past performance. For sources sought responses, teaming inquiries, or sole-source discussions,
              contact us directly.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="/FOSCHE-Capability-Statement.pdf?v=3"
                download
                data-testid="capability-download-btn"
                className="inline-flex items-center gap-3 px-6 py-3.5 bg-blueprint text-white text-sm font-medium transition-all hover:bg-[var(--gold)] hover:text-black"
              >
                <DownloadSimple size={16} /> Download PDF
              </a>
              <Link
                to="/contact"
                data-testid="gov-contact-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 border border-line text-ink text-sm font-medium transition-colors hover:border-[var(--blueprint)] hover:text-blueprint"
              >
                Contact Contracting Team <ArrowRight size={15} />
              </Link>
            </div>

            <div className="mt-12 relative" data-testid="gov-handshake-image">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/85cac4e7-9373-4ca5-a120-c8a76e0a0606/images/d50808be15dbdd6c83d415d77583df0204b1718de212e7d7a7393777fd6b10a2.png"
                alt="Service member and contractor shaking hands at a construction site"
                className="w-full h-72 md:h-96 object-cover border border-line"
              />
              <div className="absolute bottom-0 left-0 bg-[var(--gold)] text-black px-5 py-3">
                <p className="font-mono-tech text-[10px] tracking-[0.2em] uppercase">From Service to Building</p>
              </div>
            </div>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {differentiators.map((d, i) => (
                <motion.div
                  key={d.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="border border-line p-6 bg-panel"
                >
                  <d.icon size={24} className="text-blueprint mb-4" />
                  <h3 className="font-heading font-medium text-lg tracking-tight text-ink">{d.title}</h3>
                  <p className="mt-2 text-sm text-sub leading-relaxed">{d.text}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-2">
            <div className="border border-line bg-panel p-7 sticky top-28" data-testid="gov-codes-panel">
              <p className="overline-label mb-5">Registration Data</p>
              <dl className="space-y-5">
                <div>
                  <dt className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-sub">Business Type</dt>
                  <dd className="mt-1 text-sm text-ink">Service-Disabled Veteran-Owned Small Business (SDVOSB)</dd>
                </div>
                <div>
                  <dt className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-sub">UEI</dt>
                  <dd className="mt-1 text-sm text-ink" data-testid="gov-uei">{s?.uei || "SAM.gov Registered"}</dd>
                </div>
                <div>
                  <dt className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-sub">CAGE Code</dt>
                  <dd className="mt-1 text-sm text-ink" data-testid="gov-cage">{s?.cage || "Available Upon Request"}</dd>
                </div>
                <div>
                  <dt className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-sub">NAICS Codes</dt>
                  <dd className="mt-2">
                    <ul className="space-y-2.5">
                      {(s?.naics || []).map((code) => (
                        <li key={code} className="flex gap-3 text-sm">
                          <span className="font-mono-tech text-blueprint shrink-0">{code}</span>
                          <span className="text-sub">{NAICS_LABELS[code] || "Construction Services"}</span>
                        </li>
                      ))}
                    </ul>
                  </dd>
                </div>
                <div>
                  <dt className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-sub">Point of Contact</dt>
                  <dd className="mt-1 text-sm text-ink">
                    {s?.founder_name || "Terry A. Foster"} — {s?.email || "foster.terrya@fosche.us"}
                    <br />
                    {s?.phone || "313-559-5058"}
                  </dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
